import { ReactElement } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { Scholarship } from '@backrs_ui/common-ui/src/models/scholarship';

import { Box, Typography } from '@mui/material';

import { blueTextStyle, howToApplyHeaderStyle, unorderedListStyle } from './styles';

const { HOW_TO_APPLY_STRINGS, SECTION_TITLES } = appStrings.modules.nugget.scholarship;

export function HowToApply({ scholarshipInfo }: { scholarshipInfo: Scholarship }): ReactElement {
  return (
    <Box>
      <Typography variant="h4" sx={howToApplyHeaderStyle}>
        {SECTION_TITLES.howToApply}
      </Typography>
      {renderApplicationRequirements(scholarshipInfo.application_requirements)}

      {renderApplicationSubmission(
        scholarshipInfo.application_submission_method,
        scholarshipInfo.application_submission_destination,
      )}

      {renderDeadlines(
        scholarshipInfo.submission_start_date,
        scholarshipInfo.submission_deadline_priority,
        scholarshipInfo.submission_deadline,
      )}

      {renderApplicationFee(scholarshipInfo.application_fee)}

      {renderContactInformation(
        scholarshipInfo.contact_name,
        scholarshipInfo.contact_details,
        scholarshipInfo.contact_address,
      )}
    </Box>
  );
}

function renderApplicationRequirements(
  applicationRequirements: string | null,
): ReactElement | null {
  if (!applicationRequirements) {
    return null;
  }

  const requirements = applicationRequirements.split('|').map((req) => <li key={req}>{req}</li>);

  return (
    <Typography variant="body1" sx={blueTextStyle}>
      {SECTION_TITLES.applicationRequirements}
      <ul style={unorderedListStyle}>{requirements}</ul>
    </Typography>
  );
}

function truncateText(text: string | null, maxLength: 70): string | null {
  if (!text) return null;
  if (text.length <= maxLength) return text;

  return text.substring(0, maxLength - 3) + '...';
}

function renderApplicationSubmission(
  submissionMethod: string | null,
  submissionDestination: string | null,
): ReactElement | null {
  if (!submissionMethod) {
    return null;
  }

  const isEmail = submissionDestination && submissionDestination.includes('@');
  const truncatedText = truncateText(submissionDestination, 70);

  const submissionLink = submissionDestination ? (
    <a
      href={isEmail ? `mailto:${submissionDestination}` : submissionDestination}
      target="_blank"
      rel="noopener noreferrer"
    >
      {truncatedText}
    </a>
  ) : null;

  return (
    <Typography variant="body1" sx={blueTextStyle}>
      {SECTION_TITLES.applicationSubmission}
      <ul style={unorderedListStyle}>
        <li>
          {submissionMethod}
          {submissionLink ? <>: {submissionLink}</> : ''}
        </li>
      </ul>
    </Typography>
  );
}

function renderDeadlines(
  startDate: string | null,
  priorityDeadline: string | null,
  finalDeadline: string | null,
): ReactElement | null {
  if (!startDate && !priorityDeadline && !finalDeadline) {
    return null;
  }

  const deadlines = [
    startDate && `${HOW_TO_APPLY_STRINGS.SUBMISSION_START} ${startDate}`,
    finalDeadline && `${HOW_TO_APPLY_STRINGS.SUBMISSION_DEADLINE} ${finalDeadline}`,
  ].filter(Boolean);

  return (
    <Typography variant="body1" sx={blueTextStyle}>
      {SECTION_TITLES.applicationDates}
      <ul style={unorderedListStyle}>
        {deadlines.map((deadline) => (
          <li key={deadline}>{deadline}</li>
        ))}
      </ul>
    </Typography>
  );
}

function renderApplicationFee(applicationFee: number | null): ReactElement | null {
  if (!applicationFee) {
    return null;
  }

  return (
    <Typography variant="body1" sx={blueTextStyle}>
      {SECTION_TITLES.applicationFee}
      <ul style={unorderedListStyle}>
        <li>${applicationFee}</li>
      </ul>
    </Typography>
  );
}

function renderContactInformation(
  contactName: string | null,
  contactDetails: string | null,
  contactAddress: string | null,
): ReactElement | null {
  if (!contactName && !contactDetails && !contactAddress) {
    return null;
  }

  const detailsList = [];

  if (contactDetails) {
    detailsList.push(...contactDetails.split(';').map((detail) => <li key={detail}>{detail}</li>));
  }
  if (contactAddress) {
    detailsList.push(<li key="address">{contactAddress}</li>);
  }

  return (
    <Typography variant="body1" sx={blueTextStyle}>
      {contactName || HOW_TO_APPLY_STRINGS.CONTACT}
      {detailsList.length > 0 && <ul style={unorderedListStyle}>{detailsList}</ul>}
    </Typography>
  );
}
